import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTradeGroupTimeline, TradePair } from '../api/tradeGroups';
import { formatDollarAmount } from '../utils/currencyFormatter';
import './TradeActivityTimeline.css';

interface TradeActivityTimelineProps {
  tradeGroupId: string;
  productId: string;
  tradeGroupCreatedAt?: number; // Epoch millis - if provided, used as start date
}

// Creation trigger icons and colors - matches CreationTrigger enum from backend
const TRIGGER_CONFIG: Record<string, { icon: string; color: string; label: string }> = {
  // Initial ladder orders
  INITIAL_LADDER: { icon: '🔵', color: '#3b82f6', label: 'Initial' },

  // Rebalancing
  REBALANCE: { icon: '⚖️', color: '#8b5cf6', label: 'Rebalance' },

  // Adaptive mode triggers
  HOT_MONEY_REINVESTMENT: { icon: '🔥', color: '#f97316', label: 'Hot Money' },
  DIP_BUYING: { icon: '📉', color: '#22c55e', label: 'Dip Buy' },
  SCHEDULED_DEPLOYMENT: { icon: '📅', color: '#06b6d4', label: 'Scheduled' },
  PRICE_MOVEMENT_RANGE: { icon: '📊', color: '#ec4899', label: 'Price Move' },
  ADAPTIVE_RANGE_ADJUSTMENT: { icon: '🎯', color: '#a855f7', label: 'Range Adj' },

  // Standard reinvestment
  COMPLETED_PAIR_REINVESTMENT: { icon: '🔄', color: '#10b981', label: 'Reinvest' },

  // Sell side
  MATCHING_SELL_ORDER: { icon: '💰', color: '#eab308', label: 'Sell Match' },

  // Future/trailing
  FUTURE_TRADE_ACTIVATION: { icon: '⏰', color: '#06b6d4', label: 'Future' },
  TRAILING_STOP_MONITORING: { icon: '📈', color: '#ef4444', label: 'Trail Stop' },
};

const DEFAULT_TRIGGER = { icon: '⚪', color: '#6b7280', label: 'Unknown' };

const TradeActivityTimeline: React.FC<TradeActivityTimelineProps> = ({
  tradeGroupId,
  productId,
  tradeGroupCreatedAt,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ganttRef = useRef<HTMLDivElement>(null);
  const [hoveredPair, setHoveredPair] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  // Calculate date range: from trade group creation to now
  const endDate = Date.now();
  const startDate = tradeGroupCreatedAt ?? (endDate - (30 * 24 * 60 * 60 * 1000)); // Fallback to 30 days ago

  // Fetch timeline data
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trade-activity-timeline', tradeGroupId, startDate],
    queryFn: () => getTradeGroupTimeline(tradeGroupId, startDate, endDate, 60),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  // Draw spark line chart
  useEffect(() => {
    if (!data || !canvasRef.current || data.candles.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;
    const padding = 40;

    // Extract data
    const candles = data.candles;
    const times = candles.map((c) => c.timestamp);
    const prices = candles.map((c) => c.close);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const minPrice = Math.min(...prices) * 0.99; // 1% padding
    const maxPrice = Math.max(...prices) * 1.01; // 1% padding

    // Draw background - dark theme
    ctx.fillStyle = 'rgba(30, 41, 59, 0.5)';
    ctx.fillRect(0, 0, width, height);

    // TODO: Add volatility regime shading when that data is available
    // For now, just draw the price line

    // Draw price area chart
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);

    for (let i = 0; i < candles.length; i++) {
      const x = padding + ((times[i] - minTime) / (maxTime - minTime)) * (width - 2 * padding);
      const y = height - padding - ((prices[i] - minPrice) / (maxPrice - minPrice)) * (height - 2 * padding);
      ctx.lineTo(x, y);
    }

    ctx.lineTo(width - padding, height - padding);
    ctx.closePath();
    ctx.fillStyle = 'rgba(139, 92, 246, 0.25)'; // Purple with 25% opacity
    ctx.fill();

    // Draw price line
    ctx.beginPath();
    for (let i = 0; i < candles.length; i++) {
      const x = padding + ((times[i] - minTime) / (maxTime - minTime)) * (width - 2 * padding);
      const y = height - padding - ((prices[i] - minPrice) / (maxPrice - minPrice)) * (height - 2 * padding);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#a78bfa'; // Lighter purple for dark bg
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw trade group creation marker if within range
    if (data.tradeGroupCreatedAt >= minTime && data.tradeGroupCreatedAt <= maxTime) {
      const x = padding + ((data.tradeGroupCreatedAt - minTime) / (maxTime - minTime)) * (width - 2 * padding);
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.strokeStyle = '#a78bfa';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw axes - dark theme
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)';
    ctx.lineWidth = 1;
    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.stroke();
    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.stroke();

    // Draw Y-axis labels (prices) - dark theme
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(formatDollarAmount(maxPrice, productId), padding - 5, padding + 5);
    ctx.fillText(formatDollarAmount(minPrice, productId), padding - 5, height - padding + 5);

    // Draw X-axis labels (times) - dark theme
    ctx.textAlign = 'center';
    const chartStartDate = new Date(minTime);
    const chartEndDate = new Date(maxTime);
    ctx.fillText(chartStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), padding, height - padding + 20);
    ctx.fillText(chartEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), width - padding, height - padding + 20);
  }, [data, productId]);

  if (isLoading) {
    return (
      <div className="trade-activity-timeline">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading trade activity...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="trade-activity-timeline">
        <div className="error-state">
          <p className="error-message">⚠️ {error instanceof Error ? error.message : 'Failed to load trade activity'}</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || data.tradePairs.length === 0) {
    return (
      <div className="trade-activity-timeline">
        <div className="empty-state">
          <p>📊 No trade pairs yet.</p>
          <p className="empty-subtitle">Trade pairs will appear here once orders are filled.</p>
        </div>
      </div>
    );
  }

  const tradePairs = data.tradePairs;
  const allTimes = [
    ...data.candles.map((c) => c.timestamp),
    ...tradePairs.map((p) => p.buyTimestamp),
    ...tradePairs.filter((p) => p.sellTimestamp).map((p) => p.sellTimestamp!),
  ];
  const minTime = Math.min(...allTimes);
  const maxTime = Math.max(...allTimes, Date.now()); // Extend to now for open trades

  // Calculate bar positions for Gantt chart
  const calculateBarPosition = (timestamp: number) => {
    return ((timestamp - minTime) / (maxTime - minTime)) * 100;
  };

  const calculateBarWidth = (startTime: number, endTime: number | null) => {
    const end = endTime || Date.now();
    return ((end - startTime) / (maxTime - minTime)) * 100;
  };

  const formatDuration = (startTime: number, endTime: number | null) => {
    if (!endTime) return 'Ongoing';
    const durationMs = endTime - startTime;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatProfit = (pair: TradePair) => {
    if (pair.orderStatus === 'CANCELLED') return '$0.00';
    if (!pair.profit) return '--';
    const sign = pair.profit > 0 ? '+' : '';
    return `${sign}${formatDollarAmount(Math.abs(pair.profit), productId)}`;
  };

  const getTriggerConfig = (trigger: string | null | undefined) => {
    if (!trigger) return DEFAULT_TRIGGER;
    return TRIGGER_CONFIG[trigger] || DEFAULT_TRIGGER;
  };

  const getBarClass = (pair: TradePair) => {
    const classes = ['gantt-bar'];
    
    // Status-based styling
    if (pair.orderStatus === 'CANCELLED') {
      classes.push('cancelled');
    } else if (!pair.sellTimestamp) {
      classes.push('open');
    } else {
      classes.push('filled');
    }

    return classes.join(' ');
  };

  const handleBarMouseEnter = (pairId: string, event: React.MouseEvent) => {
    setHoveredPair(pairId);
    setTooltipPos({ x: event.clientX, y: event.clientY });
  };

  const handleBarMouseMove = (event: React.MouseEvent) => {
    if (hoveredPair) {
      setTooltipPos({ x: event.clientX, y: event.clientY });
    }
  };

  const handleBarMouseLeave = () => {
    setHoveredPair(null);
    setTooltipPos(null);
  };

  return (
    <div className="trade-activity-timeline">
      {/* Header */}
      <div className="timeline-header">
        <h3>📈 Trade Activity Timeline</h3>
        <div className="timeline-subtitle">
          {tradePairs.length} trade pair{tradePairs.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Spark Line Chart */}
      <div className="spark-line-container">
        <canvas ref={canvasRef} className="spark-line-canvas" />
      </div>

      {/* Gantt Chart */}
      <div className="gantt-container" ref={ganttRef}>
        <div className="gantt-timeline-axis">
          {/* Time axis labels */}
          <div className="axis-label" style={{ left: '0%' }}>
            {new Date(minTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div className="axis-label" style={{ left: '50%' }}>
            {new Date((minTime + maxTime) / 2).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div className="axis-label" style={{ left: '100%' }}>
            {new Date(maxTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>

        <div className="gantt-rows">
          {tradePairs.map((pair) => {
            const triggerConfig = getTriggerConfig(pair.creationTrigger);
            const barLeft = calculateBarPosition(pair.buyTimestamp);
            const barWidth = calculateBarWidth(pair.buyTimestamp, pair.sellTimestamp);
            const isHovered = hoveredPair === pair.buyOrderId;

            return (
              <div key={pair.buyOrderId} className="gantt-row">
                <div className="gantt-row-label">
                  <span className="trigger-icon">{triggerConfig.icon}</span>
                  <span className="trigger-label">{triggerConfig.label}</span>
                </div>
                <div className="gantt-row-timeline">
                  <div
                    className={getBarClass(pair)}
                    style={{
                      left: `${barLeft}%`,
                      width: `${barWidth}%`,
                      backgroundColor: triggerConfig.color,
                      opacity: isHovered ? 1 : 0.8,
                    }}
                    onMouseEnter={(e) => handleBarMouseEnter(pair.buyOrderId, e)}
                    onMouseMove={handleBarMouseMove}
                    onMouseLeave={handleBarMouseLeave}
                  >
                    {pair.sellTimestamp && <div className="bar-arrow">▶</div>}
                  </div>
                  <div className="profit-label" style={{ left: `${barLeft + barWidth + 1}%` }}>
                    <span className={pair.profit && pair.profit > 0 ? 'profit-positive' : pair.profit && pair.profit < 0 ? 'profit-negative' : 'profit-neutral'}>
                      {formatProfit(pair)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredPair && tooltipPos && (
        <div
          className="gantt-tooltip"
          style={{
            position: 'fixed',
            left: tooltipPos.x + 10,
            top: tooltipPos.y + 10,
          }}
        >
          {(() => {
            const pair = tradePairs.find((p) => p.buyOrderId === hoveredPair);
            if (!pair) return null;
            const triggerConfig = getTriggerConfig(pair.creationTrigger);

            return (
              <>
                <div className="tooltip-header">
                  <span className="trigger-icon">{triggerConfig.icon}</span>
                  <strong>{triggerConfig.label}</strong>
                </div>
                <div className="tooltip-row">
                  <span>Trade Pair:</span>
                  <span>{pair.buyOrderId.substring(0, 8)}...</span>
                </div>
                <div className="tooltip-row">
                  <span>Buy Order:</span>
                  <span>{pair.buyOrderId.substring(0, 12)}...</span>
                </div>
                <div className="tooltip-row">
                  <span>Buy Time:</span>
                  <span>{new Date(pair.buyTimestamp).toLocaleString()}</span>
                </div>
                <div className="tooltip-row">
                  <span>Buy Price:</span>
                  <span>{formatDollarAmount(pair.buyPrice, productId)}</span>
                </div>
                {pair.sellOrderId && (
                  <>
                    <div className="tooltip-row">
                      <span>Sell Order:</span>
                      <span>{pair.sellOrderId.substring(0, 12)}...</span>
                    </div>
                    <div className="tooltip-row">
                      <span>Sell Time:</span>
                      <span>{pair.sellTimestamp ? new Date(pair.sellTimestamp).toLocaleString() : 'Pending'}</span>
                    </div>
                    <div className="tooltip-row">
                      <span>Sell Price:</span>
                      <span>{pair.sellPrice ? formatDollarAmount(pair.sellPrice, productId) : 'Pending'}</span>
                    </div>
                  </>
                )}
                <div className="tooltip-row">
                  <span>Status:</span>
                  <span className={`status-${pair.orderStatus?.toLowerCase()}`}>{pair.orderStatus || 'FILLED'}</span>
                </div>
                <div className="tooltip-row">
                  <span>Duration:</span>
                  <span>{formatDuration(pair.buyTimestamp, pair.sellTimestamp)}</span>
                </div>
                {pair.profit !== null && (
                  <>
                    <div className="tooltip-row">
                      <span>Profit/Loss:</span>
                      <span className={pair.profit > 0 ? 'profit-positive' : pair.profit < 0 ? 'profit-negative' : 'profit-neutral'}>
                        {formatProfit(pair)} ({pair.profitPercent?.toFixed(2)}%)
                      </span>
                    </div>
                  </>
                )}
                {pair.cancellationTrigger && (
                  <div className="tooltip-row">
                    <span>Cancelled:</span>
                    <span>{pair.cancellationTrigger}</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TradeActivityTimeline;
